import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';

import { useAuth } from '../context/AuthContext';
import { useAuthUI } from '../context/AuthUIContext';
import { useCart } from '../context/CartContext';
import useAddresses from '../hooks/useAddresses';
import useShipping from '../hooks/useShipping';
import client from '../api/client';
import { stripePromise, stripeAppearance } from '../api/stripe';

import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import AddressForm from '../components/checkout/AddressForm';
import PaymentStep from '../components/checkout/PaymentStep';
import { formatPrice } from '../utils/money';
import { parseApiError } from '../utils/apiError';

function AddressPicker({ label, addresses, selectedId, onSelect, onCreate, type }) {
  const [adding, setAdding] = useState(addresses.length === 0);
  return (
    <div className="ss-checkout__section">
      <h2>{label}</h2>
      {addresses.map((a) => (
        <label key={a._id} className={`ss-choice ${selectedId === a._id ? 'is-selected' : ''}`}>
          <input
            type="radio"
            name={`addr-${type}`}
            checked={selectedId === a._id}
            onChange={() => onSelect(a._id)}
          />
          <span className="ss-choice__body">
            <span className="ss-choice__title">{a.fullName}</span>
            <span className="ss-choice__meta">
              {a.line1}{a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state} {a.postalCode}
            </span>
          </span>
        </label>
      ))}
      {adding ? (
        <AddressForm
          type={type}
          onCreate={onCreate}
          onCreated={(created) => {
            setAdding(false);
            onSelect(created._id);
          }}
          onCancel={addresses.length ? () => setAdding(false) : undefined}
        />
      ) : (
        <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>+ Add a new address</Button>
      )}
    </div>
  );
}

export default function Checkout() {
  const { user, loading: authLoading } = useAuth();
  const { openLogin } = useAuthUI();
  const {
    items,
    subtotal,
    amountDueNow,
    balanceDue,
    hasLocalPickupItems,
    hasShippableItems,
    requiresFulfillmentChoice,
    pickupDepositPercent,
  } = useCart();
  const { addresses, loading: addrLoading, create } = useAddresses();
  const { methods } = useShipping();

  // null = not yet chosen — only meaningful (and required before payment) for
  // a mixed cart (requiresFulfillmentChoice).
  const [pickupAll, setPickupAll] = useState(null);
  const [shippingAddressId, setShippingAddressId] = useState(null);
  const [billingSame, setBillingSame] = useState(true);
  const [billingAddressId, setBillingAddressId] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(null);
  const [intent, setIntent] = useState(null); // { clientSecret, orderNumber, total, amountDueAtCheckout, balanceDue }
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState(null);

  const shippingAddresses = useMemo(() => addresses.filter((a) => a.type === 'shipping'), [addresses]);
  const billingAddresses = useMemo(() => addresses.filter((a) => a.type === 'billing'), [addresses]);

  // Pure local-pickup cart, or a mixed cart where the shopper chose to pick
  // up everything — nothing to ship, so there's no address/method to collect.
  const skippingShipping =
    (hasLocalPickupItems && !hasShippableItems) || (requiresFulfillmentChoice && pickupAll === true);
  const showMethodPicker = !skippingShipping;
  // A mixed cart being split must ship via a real shipping method; a plain
  // shippable cart may still pick the "local pickup" method by choice.
  const methodChoices =
    requiresFulfillmentChoice && pickupAll === false ? methods.filter((m) => m.type === 'ship') : methods;
  const selectedMethod = methods.find((m) => m.code === shippingMethod);
  const needsShippingAddress = showMethodPicker && selectedMethod?.type !== 'pickup';
  const showBillingSameToggle = needsShippingAddress;

  // Keep the selected method valid as the choice list changes (fulfillment
  // toggle, or the method list finishing its initial load).
  useEffect(() => {
    if (!methodChoices.length) return;
    if (!shippingMethod || !methodChoices.some((m) => m.code === shippingMethod)) {
      setShippingMethod(methodChoices[0].code);
    }
  }, [methodChoices, shippingMethod]);

  // A shippable line's own depositPercent is only accurate for the default
  // split. Once "pick up everything" is chosen, it's actually charged at
  // pickupDepositPercent instead — the badge must reflect that override or
  // it'll disagree with the real Due now/Balance due later totals.
  const pickingUpEverything = requiresFulfillmentChoice && pickupAll === true;
  const displayDepositPercent = (item) =>
    pickingUpEverything && !item.localPickupOnly ? pickupDepositPercent : item.depositPercent;
  const displayDepositRequired = (item) => displayDepositPercent(item) > 0;

  const shippingCost = showMethodPicker ? (selectedMethod?.cost ?? 0) : 0;
  const total = subtotal + shippingCost;
  // The cart's own amountDueNow/balanceDue assume the default split (only
  // localPickupOnly lines picked up). Once "pick up everything" is chosen,
  // recompute from the per-line effective rate so the preview already
  // matches what checkout/intent will return — no jump once submitted.
  const previewAmountDueNow = pickingUpEverything
    ? items
        .filter((i) => i.available !== false)
        .reduce((sum, i) => {
          const pct = displayDepositPercent(i);
          return sum + (pct > 0 ? Math.round((i.lineTotal * pct) / 100) : i.lineTotal);
        }, 0)
    : amountDueNow;
  const previewBalanceDue = pickingUpEverything ? subtotal - previewAmountDueNow : balanceDue;
  // Shipping is always charged in full — deposits only apply to item price.
  // Pre-intent numbers are a preview (see useCart/cart.js) — final amounts
  // come back on the checkout/intent response.
  const amountDueDisplay = intent ? intent.amountDueAtCheckout : previewAmountDueNow + shippingCost;
  const balanceDueDisplay = intent ? intent.balanceDue : previewBalanceDue;

  // ---- gates ----
  if (authLoading) {
    return <div className="ss-container ss-state"><Spinner label="Loading" size={32} /></div>;
  }
  if (!user) {
    return (
      <section className="ss-container ss-state">
        <h2>Please log in to check out</h2>
        <p>Checkout is available to registered customers.</p>
        <p style={{ marginTop: 'var(--ss-space-4)' }}>
          <Button onClick={openLogin}>Log in</Button>
        </p>
      </section>
    );
  }
  if (items.length === 0 && !intent) {
    return (
      <section className="ss-container ss-state">
        <h2>Your cart is empty</h2>
        <p style={{ marginTop: 'var(--ss-space-4)' }}>
          <Link className="ss-btn ss-btn--primary" to="/products">Start shopping</Link>
        </p>
      </section>
    );
  }

  const fulfillmentChosen = !requiresFulfillmentChoice || pickupAll !== null;
  const canPay =
    fulfillmentChosen &&
    (needsShippingAddress ? !!shippingAddressId : true) &&
    (showMethodPicker ? !!shippingMethod : true) &&
    (showBillingSameToggle ? billingSame || !!billingAddressId : !!billingAddressId);

  const startPayment = async () => {
    setPlacing(true);
    setError(null);
    try {
      const body = {
        billingAddressId: showBillingSameToggle
          ? billingSame
            ? shippingAddressId
            : billingAddressId
          : billingAddressId,
      };
      if (requiresFulfillmentChoice) body.pickupAll = pickupAll;
      if (showMethodPicker) body.shippingMethod = shippingMethod;
      if (needsShippingAddress) body.shippingAddressId = shippingAddressId;
      const { data } = await client.post('/checkout/intent', body);
      setIntent(data);
    } catch (err) {
      setError(parseApiError(err).formError);
      setPlacing(false);
    }
  };

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)' }}>
      <h1>Checkout</h1>

      <div className="ss-checkout" style={{ marginTop: 'var(--ss-space-5)' }}>
        <div>
          {!intent ? (
            <>
              {addrLoading ? (
                <div className="ss-state"><Spinner label="Loading addresses" /></div>
              ) : (
                <>
                  {requiresFulfillmentChoice && (
                    <div className="ss-checkout__section">
                      <h2>How would you like to get your order?</h2>
                      <label className={`ss-choice ${pickupAll === true ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="fulfillment"
                          checked={pickupAll === true}
                          onChange={() => setPickupAll(true)}
                        />
                        <span className="ss-choice__body">
                          <span className="ss-choice__title">Pick up everything</span>
                          <span className="ss-choice__meta">
                            No shipping needed
                            {pickupDepositPercent > 0
                              ? ` — a ${pickupDepositPercent}% deposit applies to items picked up.`
                              : '.'}
                          </span>
                        </span>
                      </label>
                      <label className={`ss-choice ${pickupAll === false ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="fulfillment"
                          checked={pickupAll === false}
                          onChange={() => setPickupAll(false)}
                        />
                        <span className="ss-choice__body">
                          <span className="ss-choice__title">Keep separate</span>
                          <span className="ss-choice__meta">Pick up what requires it, ship the rest.</span>
                        </span>
                      </label>
                    </div>
                  )}

                  {fulfillmentChosen && (
                    <>
                      {showMethodPicker && (
                        <>
                          {needsShippingAddress && (
                            <AddressPicker
                              label="Shipping address"
                              type="shipping"
                              addresses={shippingAddresses}
                              selectedId={shippingAddressId}
                              onSelect={setShippingAddressId}
                              onCreate={create}
                            />
                          )}

                          <div className="ss-checkout__section">
                            <h2>Shipping method</h2>
                            {methodChoices.map((m) => (
                              <label key={m.code} className={`ss-choice ${shippingMethod === m.code ? 'is-selected' : ''}`}>
                                <input
                                  type="radio"
                                  name="shipping-method"
                                  checked={shippingMethod === m.code}
                                  onChange={() => setShippingMethod(m.code)}
                                />
                                <span className="ss-choice__body">
                                  <span className="ss-choice__title">{m.label} — {formatPrice(m.cost)}</span>
                                  <span className="ss-choice__meta">{m.estimate}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}

                      {showBillingSameToggle ? (
                        <>
                          <div className="ss-checkout__section">
                            <label className="ss-check">
                              <input
                                type="checkbox"
                                checked={billingSame}
                                onChange={(e) => setBillingSame(e.target.checked)}
                              />
                              <span>Billing same as shipping</span>
                            </label>
                          </div>

                          {!billingSame && (
                            <AddressPicker
                              label="Billing address"
                              type="billing"
                              addresses={billingAddresses}
                              selectedId={billingAddressId}
                              onSelect={setBillingAddressId}
                              onCreate={create}
                            />
                          )}
                        </>
                      ) : (
                        <AddressPicker
                          label="Billing address"
                          type="billing"
                          addresses={billingAddresses}
                          selectedId={billingAddressId}
                          onSelect={setBillingAddressId}
                          onCreate={create}
                        />
                      )}

                      {error && <p className="ss-form__error" role="alert">{error}</p>}
                      <Button onClick={startPayment} disabled={!canPay || placing}>
                        {placing ? <Spinner label="Preparing payment" /> : 'Continue to payment'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="ss-checkout__section">
              <h2>Payment</h2>
              {stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret, appearance: stripeAppearance }}>
                  <PaymentStep
                    orderNumber={intent.orderNumber}
                    amountDue={intent.amountDueAtCheckout}
                    onBack={() => setIntent(null)}
                  />
                </Elements>
              ) : (
                <p className="ss-form__error">Payments are not configured (missing Stripe key).</p>
              )}
            </div>
          )}
        </div>

        {/* order summary */}
        <Card className="ss-checkout__summary">
          <h2 style={{ fontSize: '1.25rem', marginBottom: 'var(--ss-space-3)' }}>Order summary</h2>
          {items.map((i) => (
            <div key={i.productId} className="ss-summary-line">
              <span>
                {i.title} × {i.quantity}
                {displayDepositRequired(i) && (
                  <Badge className="ss-deposit-badge">{displayDepositPercent(i)}% deposit</Badge>
                )}
                {i.localPickupOnly && <Badge className="ss-pickup-badge">Pickup required</Badge>}
              </span>
              <span>{formatPrice(i.lineTotal)}</span>
            </div>
          ))}
          <div className="ss-summary-line ss-summary-line--muted" style={{ marginTop: 'var(--ss-space-2)' }}>
            <span>Subtotal</span>
            <span>{formatPrice(subtotal)}</span>
          </div>
          {showMethodPicker && (
            <div className="ss-summary-line ss-summary-line--muted">
              <span>Shipping{selectedMethod ? ` (${selectedMethod.label})` : ''}</span>
              <span>{formatPrice(shippingCost)}</span>
            </div>
          )}
          {balanceDueDisplay > 0 ? (
            <>
              <div className="ss-summary-line ss-summary-line--muted">
                <span>Order total</span>
                <span>{formatPrice(intent ? intent.total : total)}</span>
              </div>
              <div className="ss-summary-total">
                <span>Due now</span>
                <span>{formatPrice(amountDueDisplay)}</span>
              </div>
              <div className="ss-summary-line ss-summary-line--muted">
                <span>Balance due later</span>
                <span>{formatPrice(balanceDueDisplay)}</span>
              </div>
            </>
          ) : (
            <div className="ss-summary-total">
              <span>Total</span>
              <span>{formatPrice(intent ? intent.total : total)}</span>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
